import React, { useEffect, useState } from "react";
import { Award, Heart } from "lucide-react";
import { api } from "../../lib/api";

/**
 * HelpfulCamperBadge — public recognition for users whose reviews and
 * replies have earned helpful-upvotes from other campers.
 *
 *   • Hidden by default — only renders once a user crosses the
 *     `helpful_camper` threshold (5 helpful upvotes received) and
 *     promotes to a gold "Helpful Legend" tier at 25.
 *   • Shows a small progress hint when fetched for the OWN profile
 *     (so users know how close they are to the next tier).
 *   • Drop-in: pass `userId` to view someone else, or `self` for the
 *     current camper.
 */
export default function HelpfulCamperBadge({ userId = null, self = false, className = "" }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    const url = self
      ? "/me/helpful-stats"
      : userId
        ? `/users/${encodeURIComponent(userId)}/helpful-stats`
        : null;
    if (!url) return;
    api.get(url).then(({ data }) => {
      if (alive) setStats(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [userId, self]);

  if (!stats) return null;

  const showProgress = self && !stats.badge && stats.next_threshold != null;

  if (!stats.badge && !showProgress) return null;

  if (stats.badge) {
    const isLegend = stats.badge === "helpful_legend";
    return (
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 border hairline ${
          isLegend
            ? "bg-[#fef3c7] border-[#fbbf24] text-[#78350f]"
            : "bg-[#dcfce7] border-[#16a34a] text-[#14532d]"
        } ${className}`}
        data-testid={`helpful-camper-badge-${stats.badge}`}
        title={`Earned ${stats.score} helpful upvotes from other campers`}
      >
        {isLegend ? <Award className="w-3.5 h-3.5" /> : <Heart className="w-3.5 h-3.5" />}
        <span className="mono text-[10px] font-bold uppercase tracking-[0.15em]">
          {isLegend ? "Helpful legend" : "Helpful camper"}
        </span>
        <span className="mono text-[10px] text-[#525252]">· {stats.score}</span>
      </div>
    );
  }

  // Self-view progress hint — only nudges the user, doesn't render publicly.
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border hairline bg-white text-[#525252] ${className}`}
      data-testid="helpful-camper-progress"
      title="Earn helpful-upvotes on your reviews & replies to unlock the badge"
    >
      <Heart className="w-3.5 h-3.5" />
      <span className="mono text-[10px] uppercase tracking-[0.15em]">
        {stats.score} / {stats.next_threshold} to Helpful camper
      </span>
    </div>
  );
}
